import type {
  KitListResponse,
  AssignDoctorRequest,
  AssignDoctorResponse,
  NoteResponse,
  BadgeCounts,
  OrdersTab,
} from "../types/orders";
import { apiFetch } from "./client";

const BASE = "/api/v1/admin/orders";

function buildQuery(search: string, page: number, pageLimit: number): string {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  params.set("page", String(page));
  params.set("page_limit", String(pageLimit));
  return params.toString();
}

// ── List endpoints ──────────────────────────────────────────────────

export async function listOrders(search = "", page = 1, pageLimit = 10): Promise<KitListResponse> {
  return apiFetch<KitListResponse>(`${BASE}?${buildQuery(search, page, pageLimit)}`);
}

export async function listSamplesAtLab(search = "", page = 1, pageLimit = 10): Promise<KitListResponse> {
  return apiFetch<KitListResponse>(`${BASE}/samples-at-lab?${buildQuery(search, page, pageLimit)}`);
}

export async function listResultsReceived(search = "", page = 1, pageLimit = 10): Promise<KitListResponse> {
  return apiFetch<KitListResponse>(`${BASE}/results-received?${buildQuery(search, page, pageLimit)}`);
}

export async function listReportsPublished(search = "", page = 1, pageLimit = 10): Promise<KitListResponse> {
  return apiFetch<KitListResponse>(`${BASE}/reports-published?${buildQuery(search, page, pageLimit)}`);
}

// ── Assignment endpoints ────────────────────────────────────────────

export async function assignDoctor(kitRecordId: string, payload: AssignDoctorRequest): Promise<AssignDoctorResponse> {
  return apiFetch<AssignDoctorResponse>(`${BASE}/${kitRecordId}/assign`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function reassignDoctor(kitRecordId: string, payload: AssignDoctorRequest): Promise<AssignDoctorResponse> {
  return apiFetch<AssignDoctorResponse>(`${BASE}/${kitRecordId}/assign`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function unassignDoctor(kitRecordId: string, kitCode: string): Promise<void> {
  const qs = kitCode ? `?kit_code=${encodeURIComponent(kitCode)}` : "";
  await apiFetch<void>(`${BASE}/${kitRecordId}/assign${qs}`, { method: "DELETE" });
}

// ── Note endpoints ──────────────────────────────────────────────────

export async function createNote(kitRecordId: string, noteText: string): Promise<NoteResponse> {
  return apiFetch<NoteResponse>(`${BASE}/${kitRecordId}/notes`, {
    method: "POST",
    body: JSON.stringify({ note_text: noteText }),
  });
}

export async function updateNote(kitRecordId: string, noteText: string): Promise<NoteResponse> {
  return apiFetch<NoteResponse>(`${BASE}/${kitRecordId}/notes`, {
    method: "PUT",
    body: JSON.stringify({ note_text: noteText }),
  });
}

export async function deleteNote(kitRecordId: string): Promise<void> {
  await apiFetch<void>(`${BASE}/${kitRecordId}/notes`, { method: "DELETE" });
}

// ── Badge endpoints ─────────────────────────────────────────────────

/**
 * Fetch unread/badge counts.
 *
 * Pass `{ background: true }` ONLY for caller-driven polling (e.g. the
 * 30s interval inside `useBadgeCounts`). User-triggered refreshes (route
 * change, explicit reload) MUST omit the flag — otherwise their request
 * tells the backend to skip the sliding-window touch and the user gets
 * a premature 401 on the next idle check (HH-200 Unit 28).
 */
export async function getBadgeCounts(
  opts: { background?: boolean } = {},
): Promise<BadgeCounts> {
  const headers = opts.background ? { "X-Activity": "background" } : undefined;
  return apiFetch<BadgeCounts>(`${BASE}/badges`, headers ? { headers } : {});
}

export async function markTabViewed(tab: OrdersTab): Promise<void> {
  await apiFetch<void>(`${BASE}/badges/${tab}`, { method: "PATCH" });
}
